// components/ui/Toast/toast.tsx
import * as React from "react"
import { cn } from "../../../lib/utils"
import { CheckCircle, XCircle, X } from 'lucide-react'

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'destructive'
  title?: string
  description?: string
  onClose?: () => void
  duration?: number
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ className, variant = 'default', title, description, onClose, duration = 3000, ...props }, ref) => {
    React.useEffect(() => {
      const timer = setTimeout(() => {
        onClose?.()
      }, duration)

      return () => clearTimeout(timer)
    }, [onClose, duration])

    return (
      <div
        ref={ref}
        className={cn(
          "pointer-events-auto relative flex w-full items-center justify-between overflow-hidden rounded-md border p-4 shadow-lg transition-all",
          variant === 'default' && "bg-green-50 border-green-200",
          variant === 'destructive' && "bg-red-50 border-red-200",
          className
        )}
        {...props}
      >
        <div className="flex items-start gap-3 w-full pr-6">
          {variant === 'default' ? (
            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          
          <div className="flex flex-col gap-1">
            {title && (
              <div className={cn(
                "text-sm font-medium",
                variant === 'default' && "text-green-800",
                variant === 'destructive' && "text-red-800"
              )}>
                {title}
              </div>
            )}
            {description && (
              <div className={cn(
                "text-sm",
                variant === 'default' && "text-green-700",
                variant === 'destructive' && "text-red-700"
              )}>
                {description}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={onClose}
          className={cn(
            "absolute right-2 top-2 rounded-md p-1",
            variant === 'default' && "text-green-600 hover:bg-green-100",
            variant === 'destructive' && "text-red-600 hover:bg-red-100"
          )}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    )
  }
)
Toast.displayName = "Toast"